import { PALETTE_NAMES } from "./palette";

/**
 * Indicator methods of the engine, put on it like the layers
 * (`Object.assign(engine, createIndicatorApi(engine))`). The list changes
 * come through engine.events.onIndicatorsChange.
 *
 * @param {object} engine - ChartEngine: indicators (createIndicators), data
 */
export const createIndicatorApi = (engine) => ({
  /**
   * @param {string} id - registry id (indicators/registry.js), e.g. "moving-average"
   * @param {object} [inputs] - { [input key]: value } over the PO defaults
   * @param {{ plots?: object, levels?: object[] }} [styles] - over the default styles:
   *   plots { [plot key]: { isVisible, color, downColor, width } }, levels [{ value, color, width, isVisible }]
   * @returns {string|null} key of the new indicator; null for an unknown id or one not calculated yet
   */
  addIndicator: (id, inputs, styles) => engine.indicators.add(id, inputs, styles),

  /** @returns {boolean} false for an unknown key */
  removeIndicator: (key) => engine.indicators.remove(key),

  /** Removes every indicator (the «Delete all» of the indicators menu). */
  removeAllIndicators: () => engine.indicators.removeAll(),

  /**
   * @param {string} key
   * @param {{ inputs?: object, styles?: object }} changes - like addIndicator, over the current ones
   * @returns {boolean} false for an unknown key
   */
  updateIndicator: (key, changes) => engine.indicators.update(key, changes),

  /** @returns {boolean} false for an unknown key */
  setIndicatorVisible: (key, isVisible) => engine.indicators.setVisible(key, isVisible),

  /**
   * Expand (180 px) or the normal height (120 px) of an oscillator pane (D24).
   *
   * @param {string} key - of the oscillator (its paneId)
   * @param {boolean} isExpanded
   */
  setPaneExpanded: (key, isExpanded) => engine.paneStack.setExpanded(key, Boolean(isExpanded)),

  /** @returns {object|null} places of the oscillator panes, see events.onPaneLayoutChange */
  getPaneLayout: () => engine.events.getPaneLayout(),

  /** @returns {object[]} key, id, name, isOscillator, paneId, inputs, styles, isVisible, plotKeys */
  getIndicators: () => engine.indicators.list(),

  /**
   * The 11 colours of indicator lines in the order of PO (po-chart-visual-spec.md §7.3), as
   * the styles keep them: a picked one goes to updateIndicator unchanged.
   *
   * @returns {string[]} CSS colours
   */
  getLinePalette: () => PALETTE_NAMES.map((name) => engine.theme.palette[name].css),

  /**
   * Values for comparing with TradingView or PO.
   *
   * @param {string} key
   * @returns {Array<{ time: number, values: Array<number|null> }> | null} per stored bar: open time in
   *   UTC seconds, a value per plot in the order of plotKeys (getIndicators); null for an unknown key
   */
  getIndicatorValues: (key) => engine.indicators.values(key, engine.data.store.bars),
});
